/**
 * Утилита для генерации случайных сумм в диапазонах
 */

/**
 * Генерирует случайное число в диапазоне [min, max]
 * @param {number} min - Минимальное значение
 * @param {number} max - Максимальное значение
 * @param {number} decimals - Количество знаков после запятой (по умолчанию 2)
 * @returns {number} Случайное число в диапазоне
 */
export const generateRandomAmount = (min, max, decimals = 2) => {
    if (min === undefined || max === undefined || min === null || max === null) {
        return null;
    }
    if (min > max) {
        // Если min > max, меняем местами
        [min, max] = [max, min];
    }
    const random = Math.random() * (max - min) + min;
    return parseFloat(random.toFixed(decimals));
};

/**
 * Генерирует все суммы для указанного тарифа на основе диапазонов из настроек
 * @param {string} tariffLabel - Метка тарифа (например, "💰 1 ТАРИФ")
 * @param {object} siteSettings - Настройки сайта с диапазонами
 * @returns {object} Объект с сгенерированными суммами
 */
export const generateAmountsForTariff = (tariffLabel, siteSettings) => {
    if (!siteSettings?.randomizeRanges) {
        return null; // Если рандомизация выключена, возвращаем null
    }

    const amounts = {
        tariff: tariffLabel,
        generatedAt: new Date().toISOString()
    };

    // Выигрыш пользователя
    const baseRange = siteSettings.tariffBaseAmountsRanges?.[tariffLabel];
    if (baseRange?.min !== undefined && baseRange?.max !== undefined) {
        amounts.tariffBaseAmount = generateRandomAmount(baseRange.min, baseRange.max, 2);
    }

    // Депозитная сумма
    const depositRange = siteSettings.tariffDepositAmountsRanges?.[tariffLabel];
    if (depositRange?.min !== undefined && depositRange?.max !== undefined) {
        amounts.tariffDepositAmount = generateRandomAmount(depositRange.min, depositRange.max, 0);
    }

    // Размер комиссии
    const commissionRange = siteSettings.tariffCommissionsRanges?.[tariffLabel];
    if (commissionRange?.min !== undefined && commissionRange?.max !== undefined) {
        amounts.tariffCommission = generateRandomAmount(commissionRange.min, commissionRange.max, 2);
    }

    // Сумма верификации
    const verificationRange = siteSettings.tariffVerificationAmountsRanges?.[tariffLabel];
    if (verificationRange?.min !== undefined && verificationRange?.max !== undefined) {
        amounts.tariffVerificationAmount = generateRandomAmount(verificationRange.min, verificationRange.max, 2);
    }

    // Дополнительная сумма
    const additionalRange = siteSettings.tariffAdditionalAmountsRanges?.[tariffLabel];
    if (additionalRange?.min !== undefined && additionalRange?.max !== undefined) {
        amounts.tariffAdditionalAmount = generateRandomAmount(additionalRange.min, additionalRange.max, 2);
    }

    // Процент налога
    const taxPercentRange = siteSettings.tariffTaxPercentsRanges?.[tariffLabel];
    if (taxPercentRange?.min !== undefined && taxPercentRange?.max !== undefined) {
        amounts.tariffTaxPercent = generateRandomAmount(taxPercentRange.min, taxPercentRange.max, 1);
    }

    // Комиссия за конвертацию
    const conversionRange = siteSettings.tariffConversionCommissionsRanges?.[tariffLabel];
    if (conversionRange?.min !== undefined && conversionRange?.max !== undefined) {
        amounts.tariffConversionCommission = generateRandomAmount(conversionRange.min, conversionRange.max, 2);
    }

    // Банковская комиссия
    const bankFeeRange = siteSettings.tariffBankFeesRanges?.[tariffLabel];
    if (bankFeeRange?.min !== undefined && bankFeeRange?.max !== undefined) {
        amounts.tariffBankFee = generateRandomAmount(bankFeeRange.min, bankFeeRange.max, 2);
    }

    // Оплата работы брокера
    const brokerFeeRange = siteSettings.tariffBrokerFeesRanges?.[tariffLabel];
    if (brokerFeeRange?.min !== undefined && brokerFeeRange?.max !== undefined) {
        amounts.tariffBrokerFee = generateRandomAmount(brokerFeeRange.min, brokerFeeRange.max, 2);
    }

    // Процент от выигрыша
    const winningPercentRange = siteSettings.tariffWinningPercentagesRanges?.[tariffLabel];
    if (winningPercentRange?.min !== undefined && winningPercentRange?.max !== undefined) {
        amounts.tariffWinningPercentage = generateRandomAmount(winningPercentRange.min, winningPercentRange.max, 0);
    }

    // Генерируем суммы для кастомных модалов
    if (siteSettings.modalTemplates) {
        siteSettings.modalTemplates.forEach(template => {
            if (template.financeFieldName && template.actionKey && template.enabled !== false) {
                const actionKeyCapitalized = template.actionKey.charAt(0).toUpperCase() + template.actionKey.slice(1);
                const rangesKey = `tariff${actionKeyCapitalized}AmountsRanges`;
                const fieldKey = `tariff${actionKeyCapitalized}Amount`;
                
                const customRange = siteSettings[rangesKey]?.[tariffLabel];
                if (customRange?.min !== undefined && customRange?.max !== undefined) {
                    amounts[fieldKey] = generateRandomAmount(customRange.min, customRange.max, 2);
                }
            }
        });
    }

    return amounts;
};

/**
 * Получает значение суммы для тарифа, используя либо generatedAmounts, либо фиксированные значения из настроек
 * @param {string} tariffLabel - Метка тарифа
 * @param {string} fieldKey - Ключ поля в generatedAmounts (например, "tariffBaseAmount")
 * @param {object} generatedAmounts - Объект с сгенерированными суммами пользователя
 * @param {object} siteSettings - Настройки сайта
 * @returns {number|null} Значение суммы или null
 */
export const getAmountForTariff = (tariffLabel, fieldKey, generatedAmounts, siteSettings) => {
    // Если есть сгенерированные суммы для этого тарифа - используем их
    if (generatedAmounts && generatedAmounts.tariff === tariffLabel) {
        const value = generatedAmounts[fieldKey];
        if (value !== undefined && value !== null) {
            return value;
        }
    }

    // Иначе используем фиксированные значения из настроек
    // Преобразуем ключ из generatedAmounts в ключ настроек
    // Например: "tariffBaseAmount" -> "tariffBaseAmounts"
    const settingsKey = fieldKey + 's';
    return siteSettings?.[settingsKey]?.[tariffLabel] || null;
};

